"use client";

import { useEffect, useState, useCallback } from "react";
import TopBar from "@/components/TopBar";
import CoinIcon from "@/components/CoinIcon";

type Player = {
  id: string;
  name: string;
  coins: number;
  redeemed: number;
  earned: number;
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  // Probe auth by attempting an admin-only no-op (creating nothing). We instead
  // remember a flag in sessionStorage after a successful login.
  useEffect(() => {
    setAuthed(sessionStorage.getItem("rbx_admin_ok") === "1");
    setChecking(false);
  }, []);

  if (checking) return null;

  return (
    <main className="arena-bg relative flex-1 flex flex-col">
      <TopBar title="ADMIN" />
      {authed ? (
        <AdminPanel onLogout={() => setAuthed(false)} />
      ) : (
        <LoginGate onSuccess={() => setAuthed(true)} />
      )}
    </main>
  );
}

function LoginGate({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await fetch("/api/auth", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    setBusy(false);
    if (res.ok) {
      sessionStorage.setItem("rbx_admin_ok", "1");
      onSuccess();
    } else {
      const j = await res.json().catch(() => ({}));
      setError(j.error || "Login failed");
    }
  }

  return (
    <div className="flex-1 flex items-center justify-center px-6">
      <form onSubmit={submit} className="panel p-8 w-full max-w-sm">
        <h2 className="headline text-2xl text-white mb-1">STAFF ACCESS</h2>
        <p className="text-white/50 text-sm mb-5">Enter the admin password.</p>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full bg-navy-deep/80 border border-electric/30 rounded-lg px-4 py-3 outline-none focus:border-electric mb-3"
        />
        {error && <p className="text-energy text-sm mb-3">{error}</p>}
        <button disabled={busy} className="btn btn-red w-full">
          {busy ? "..." : "ENTER"}
        </button>
      </form>
    </div>
  );
}

function AdminPanel({ onLogout }: { onLogout: () => void }) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string>("");

  const load = useCallback(async () => {
    const res = await fetch("/api/players", { cache: "no-store" });
    const j = await res.json();
    setPlayers(j.players ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function flash(m: string) {
    setMsg(m);
    setTimeout(() => setMsg(""), 2200);
  }

  async function addPlayer(e: React.FormEvent) {
    e.preventDefault();
    const clean = name.trim();
    if (!clean) return;
    const res = await fetch("/api/players", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: clean }),
    });
    if (res.ok) {
      setName("");
      flash(`Added ${clean}`);
      load();
    } else {
      flash("Could not add player");
    }
  }

  async function logout() {
    await fetch("/api/auth", { method: "DELETE" });
    sessionStorage.removeItem("rbx_admin_ok");
    onLogout();
  }

  return (
    <div className="flex-1 px-5 sm:px-8 py-6 max-w-4xl w-full mx-auto">
      <div className="flex items-center justify-between mb-5">
        <form onSubmit={addPlayer} className="flex gap-2 flex-1 max-w-md">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="New player name"
            className="flex-1 bg-navy/70 border border-electric/30 rounded-lg px-4 py-2.5 outline-none focus:border-electric"
          />
          <button className="btn btn-blue">+ ADD</button>
        </form>
        <button
          onClick={logout}
          className="btn btn-ghost text-xs py-1.5 px-3 ml-3"
        >
          LOG OUT
        </button>
      </div>

      {msg && (
        <div className="mb-4 text-sun font-display tracking-widest animate-pop">
          {msg}
        </div>
      )}

      {loading ? (
        <p className="text-white/40">Loading…</p>
      ) : players.length === 0 ? (
        <p className="text-white/40">No players yet. Add one above.</p>
      ) : (
        <div className="space-y-3">
          {players.map((p) => (
            <PlayerRow key={p.id} player={p} onChange={load} onFlash={flash} />
          ))}
        </div>
      )}
    </div>
  );
}

function PlayerRow({
  player,
  onChange,
  onFlash,
}: {
  player: Player;
  onChange: () => void;
  onFlash: (m: string) => void;
}) {
  const [amount, setAmount] = useState(1);
  const [busy, setBusy] = useState(false);

  async function adjust(action: "give" | "take") {
    if (amount <= 0) return;
    setBusy(true);
    const res = await fetch("/api/coins", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: player.id, amount, action }),
    });
    setBusy(false);
    if (res.ok) {
      onFlash(
        `${action === "give" ? "Gave" : "Took"} ${amount} ${
          amount === 1 ? "coin" : "coins"
        } ${action === "give" ? "to" : "from"} ${player.name}`,
      );
      onChange();
    } else {
      onFlash("Action failed");
    }
  }

  async function remove() {
    if (!confirm(`Remove ${player.name}? This cannot be undone.`)) return;
    const res = await fetch("/api/players", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: player.id }),
    });
    if (res.ok) {
      onFlash(`Removed ${player.name}`);
      onChange();
    }
  }

  return (
    <div className="panel p-4 flex flex-wrap items-center gap-4">
      <div className="flex-1 min-w-[140px]">
        <div className="font-display text-xl text-white">{player.name}</div>
        <div className="text-xs text-white/45 tracking-wide">
          redeemed {player.redeemed} · earned {player.earned}
        </div>
      </div>

      <div className="coin-chip text-2xl">
        <CoinIcon size={26} /> {player.coins}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(Math.max(1, Number(e.target.value) || 1))}
          className="w-16 bg-navy-deep/80 border border-white/15 rounded-md px-2 py-2 text-center outline-none focus:border-electric"
        />
        <button
          disabled={busy}
          onClick={() => adjust("give")}
          className="btn btn-blue text-sm py-2 px-3"
        >
          GIVE
        </button>
        <button
          disabled={busy}
          onClick={() => adjust("take")}
          className="btn btn-red text-sm py-2 px-3"
        >
          TAKE
        </button>
        <button
          onClick={remove}
          title="Remove player"
          className="btn btn-ghost text-sm py-2 px-2.5"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
